import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SearchableSelect from '../components/SearchableFacilitySelect';
import { modalityProcedureMappingService, procedureService, vendorService } from '../services/dataService';
import styles from './assets/css/EquipmentProcedureMappingPage.module.css';

const initialForm = {
  procedure_id: '',
  sha_procedure_code: '',
  is_capable: true,
  processing_time_minutes: '',
  notes: '',
  description_keywords: '',
};

const getErrorMessage = (err, fallback) =>
  err?.response?.data?.detail || err?.message || fallback;

const toLabel = (value) => (value || '').replace(/_/g, ' ').toUpperCase();

const parseKeywords = (value) => value.split(',').map((keyword) => keyword.trim()).filter(Boolean);

const KEYWORD_HELP = 'Comma-separated words matched against the EMR request description. '
  + "When a procedure is mapped to several modalities, a match routes it here. '*' matches any letters, "
  + "e.g. *ray* (X-ray, Xray) or ultra* (ultrasound).";

const EXCLUDE_ALREADY_MAPPED_PROCEDURES = true;
const PROCEDURE_CATALOG_LIMIT = 500;

const getProcedureCode = (procedure) => procedure.procedure_code || procedure.code || '';

const getProcedureLabel = (procedure) => {
  const code = getProcedureCode(procedure);
  return [code, procedure.name].filter(Boolean).join(' - ');
};

const getProcedureSearchText = (procedure) => [
  procedure.id,
  getProcedureCode(procedure),
  procedure.name,
  procedure.description,
  procedure.category,
  procedure.modality,
].filter(Boolean).join(' ');

const ModalityProcedureMappingPage = () => {
  // ── Modalities ──────────────────────────────────────────────────────────────
  const [modalities, setModalities] = useState([]);
  const [modalitiesLoading, setModalitiesLoading] = useState(false);
  const [selectedModality, setSelectedModality] = useState(null);

  // ── Procedure catalog ───────────────────────────────────────────────────────
  const [allProcedures, setAllProcedures] = useState([]);
  const [proceduresLoading, setProceduresLoading] = useState(false);
  const [proceduresError, setProceduresError] = useState('');

  // ── Equipment with this modality ────────────────────────────────────────────
  // ── Mappings for selected modality ─────────────────────────────────────────
  const [mappings, setMappings] = useState([]);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [mappingSearch, setMappingSearch] = useState('');
  const [capabilityFilter, setCapabilityFilter] = useState('');
  const [mappingSort, setMappingSort] = useState({ key: 'sha_procedure_code', direction: 'asc' });
  const [mappingPage, setMappingPage] = useState(1);
  const [mappingPageSize, setMappingPageSize] = useState(10);

  // ── Add form ─────────────────────────────────────────────────────────────────
  const [form, setForm] = useState(initialForm);
  const [formLoading, setFormLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [error, setError] = useState('');

  // ── Remove ──────────────────────────────────────────────────────────────────
  const [removingId, setRemovingId] = useState(null);

  // ── Edit description keywords ───────────────────────────────────────────────
  const [editingKeywordsId, setEditingKeywordsId] = useState(null);
  const [keywordsDraft, setKeywordsDraft] = useState('');
  const [savingKeywordsId, setSavingKeywordsId] = useState(null);

  // ──────────────────────────────────────────────────────────────────────────
  // Data loading
  // ──────────────────────────────────────────────────────────────────────────

  const loadModalities = useCallback(async () => {
    setModalitiesLoading(true);
    try {
      const data = await vendorService.getDropdownConfig({ category: 'modality', status: 'active' });
      setModalities(data.items || []);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load modalities'));
    } finally {
      setModalitiesLoading(false);
    }
  }, []);

  const loadAllProcedures = useCallback(async () => {
    setProceduresLoading(true);
    setProceduresError('');
    try {
      const data = await procedureService.getAll(0, PROCEDURE_CATALOG_LIMIT);
      setAllProcedures(Array.isArray(data) ? data : data?.items || []);
    } catch (err) {
      setAllProcedures([]);
      setProceduresError(getErrorMessage(err, 'Failed to load procedure catalog'));
    } finally {
      setProceduresLoading(false);
    }
  }, []);

  const loadMappings = useCallback(async (modality) => {
    if (!modality) return;
    setMappingsLoading(true);
    setError('');
    try {
      const data = await modalityProcedureMappingService.getAll({ modality_id: modality.id });
      setMappings(data || []);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load procedure mappings'));
    } finally {
      setMappingsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadModalities();
    loadAllProcedures();
  }, [loadModalities, loadAllProcedures]);

  // ──────────────────────────────────────────────────────────────────────────
  // Modality selection
  // ──────────────────────────────────────────────────────────────────────────

  const handleSelectModality = (mod) => {
    setSelectedModality(mod);
    setMappings([]);
    setForm(initialForm);
    setSuccessMsg('');
    setError('');
    setMappingSearch('');
    setCapabilityFilter('');
    setMappingPage(1);
    loadMappings(mod);
  };

  // ──────────────────────────────────────────────────────────────────────────
  // When a procedure is picked from the dropdown, auto-fill sha_procedure_code
  // ──────────────────────────────────────────────────────────────────────────

  const handleProcedureSelect = (procedureId) => {
    const proc = allProcedures.find((p) => String(p.id) === String(procedureId));
    setForm((prev) => ({
      ...prev,
      procedure_id: procedureId,
      sha_procedure_code: proc?.procedure_code || proc?.code || prev.sha_procedure_code,
    }));
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Add mapping
  // ──────────────────────────────────────────────────────────────────────────

  const handleAddMapping = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    if (!form.procedure_id) {
      setError('Select a procedure from the catalog');
      return;
    }
    if (!form.sha_procedure_code.trim()) {
      setError('SHA procedure code is required');
      return;
    }

    setFormLoading(true);
    try {
      const payload = {
        modality_id: selectedModality.id,
        procedure_id: form.procedure_id,
        sha_procedure_code: form.sha_procedure_code.trim(),
        is_capable: form.is_capable,
        processing_time_minutes: form.processing_time_minutes
          ? parseInt(form.processing_time_minutes, 10)
          : null,
        notes: form.notes.trim() || null,
        description_keywords: parseKeywords(form.description_keywords),
      };
      await modalityProcedureMappingService.create(payload);
      setSuccessMsg(`Procedure "${form.sha_procedure_code}" mapped to ${selectedModality.code} successfully.`);
      setForm(initialForm);
      await loadMappings(selectedModality);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to add procedure mapping'));
    } finally {
      setFormLoading(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Remove mapping
  // ──────────────────────────────────────────────────────────────────────────

  const handleRemoveMapping = async (mapping) => {
    if (!window.confirm(`Remove procedure "${mapping.sha_procedure_code}" from ${selectedModality.code}?`)) return;
    setRemovingId(mapping.id);
    setError('');
    setSuccessMsg('');
    try {
      await modalityProcedureMappingService.delete(mapping.id);
      setSuccessMsg(`Procedure "${mapping.sha_procedure_code}" removed.`);
      await loadMappings(selectedModality);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to remove procedure mapping'));
    } finally {
      setRemovingId(null);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Edit description keywords
  // ──────────────────────────────────────────────────────────────────────────

  const startEditingKeywords = (mapping) => {
    setEditingKeywordsId(mapping.id);
    setKeywordsDraft((mapping.description_keywords || []).join(', '));
  };

  const handleSaveKeywords = async (mapping) => {
    setSavingKeywordsId(mapping.id);
    setError('');
    setSuccessMsg('');
    try {
      await modalityProcedureMappingService.update(mapping.id, {
        description_keywords: parseKeywords(keywordsDraft),
      });
      setSuccessMsg(`Description keywords for "${mapping.sha_procedure_code}" saved.`);
      setEditingKeywordsId(null);
      await loadMappings(selectedModality);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to save description keywords'));
    } finally {
      setSavingKeywordsId(null);
    }
  };

  const mappedProcedureIds = new Set(mappings.map((m) => String(m.procedure_id)));
  const availableProcedures = EXCLUDE_ALREADY_MAPPED_PROCEDURES
    ? allProcedures.filter((p) => !mappedProcedureIds.has(String(p.id)))
    : allProcedures;
  const procedureCatalogUnavailable = !proceduresLoading && allProcedures.length === 0;
  const noAvailableProcedures = !proceduresLoading && allProcedures.length > 0 && availableProcedures.length === 0;
  const filteredMappings = useMemo(() => {
    const term = mappingSearch.trim().toLowerCase();
    return mappings.filter((mapping) => {
      if (capabilityFilter === 'capable' && !mapping.is_capable) return false;
      if (capabilityFilter === 'not_capable' && mapping.is_capable) return false;
      if (!term) return true;
      return [
        mapping.sha_procedure_code,
        mapping.procedure_name,
        mapping.processing_time_minutes,
        mapping.notes,
        ...(mapping.description_keywords || []),
      ].filter((value) => value != null).join(' ').toLowerCase().includes(term);
    });
  }, [capabilityFilter, mappingSearch, mappings]);

  const sortedMappings = useMemo(() => {
    const direction = mappingSort.direction === 'asc' ? 1 : -1;
    return [...filteredMappings].sort((left, right) => {
      const leftValue = left[mappingSort.key];
      const rightValue = right[mappingSort.key];
      if (typeof leftValue === 'boolean' || typeof rightValue === 'boolean') {
        return (Number(Boolean(leftValue)) - Number(Boolean(rightValue))) * direction;
      }
      return String(leftValue ?? '').localeCompare(
        String(rightValue ?? ''),
        undefined,
        { numeric: true, sensitivity: 'base' },
      ) * direction;
    });
  }, [filteredMappings, mappingSort]);

  const mappingTotalPages = Math.max(1, Math.ceil(sortedMappings.length / mappingPageSize));
  const currentMappingPage = Math.min(mappingPage, mappingTotalPages);
  const paginatedMappings = useMemo(() => {
    const start = (currentMappingPage - 1) * mappingPageSize;
    return sortedMappings.slice(start, start + mappingPageSize);
  }, [currentMappingPage, mappingPageSize, sortedMappings]);

  const changeMappingSort = (key) => {
    setMappingSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
    setMappingPage(1);
  };

  const sortIndicator = (key) => (
    mappingSort.key === key ? (mappingSort.direction === 'asc' ? ' ↑' : ' ↓') : ''
  );

  // ──────────────────────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────────────────────

  return (
    <div className={styles.page}>
      <h1>Modality–Procedure Mapping</h1>
      <p className={styles.subtitle}>
        Map SHA procedures to modalities. Every machine enrolled under a modality automatically
        inherits its procedure mappings.
      </p>

      <div className={styles.layout}>
        {/* ── Modality selector panel ──────────────────────────────────────── */}
        <div className={styles.sidePanel}>
          <div className={styles.sidePanelHeader}>
            <h2>Modalities</h2>
          </div>

          {modalitiesLoading ? (
            <p className={styles.hint}>Loading…</p>
          ) : (
            <ul className={styles.equipList}>
              {modalities.length === 0 && (
                <li className={styles.hint}>No active modalities found.</li>
              )}
              {modalities.map((mod) => (
                <li
                  key={mod.id}
                  className={`${styles.equipItem} ${selectedModality?.id === mod.id ? styles.equipItemActive : ''}`}
                  onClick={() => handleSelectModality(mod)}
                >
                  <span className={styles.equipName}>{mod.code}</span>
                  <span className={styles.equipMeta}>{mod.name}</span>
                  <span className={`${styles.badge} ${styles.badge_operational}`}>
                    {toLabel(mod.status)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Right: mapping detail ─────────────────────────────────────────── */}
        <div className={styles.mainPanel}>
          {!selectedModality ? (
            <div className={styles.emptyState}>
              <p>Select a modality from the list to manage its procedure mappings.</p>
              <p className={styles.hint}>
                Procedures mapped here apply to <strong>all equipment</strong> assigned to that modality.
              </p>
            </div>
          ) : (
            <>
              {/* Modality info */}
              <div className={styles.panel}>
                <div className={styles.equipInfo}>
                  <div>
                    <strong>{selectedModality.code}</strong>
                    <span className={styles.infoSep}>&bull;</span>
                    <span>{selectedModality.name}</span>
                  </div>
                  <span className={`${styles.badge} ${styles.badge_operational}`}>
                    {toLabel(selectedModality.status)}
                  </span>
                </div>

                {/* Equipment list for this modality */}
              </div>

              {/* Current mappings */}
              <div className={`${styles.panel} ${styles.mappingTablePanel}`}>
                <div className={styles.panelHeader}>
                  <h2>Mapped Procedures</h2>
                  <span className={styles.count}>
                    {filteredMappings.length} of {mappings.length} procedure{mappings.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className={styles.tableControls}>
                  <input
                    type="search"
                    placeholder="Search code, procedure, time, notes, or keywords"
                    value={mappingSearch}
                    onChange={(event) => {
                      setMappingSearch(event.target.value);
                      setMappingPage(1);
                    }}
                  />
                  <select
                    value={capabilityFilter}
                    onChange={(event) => {
                      setCapabilityFilter(event.target.value);
                      setMappingPage(1);
                    }}
                  >
                    <option value="">All capabilities</option>
                    <option value="capable">Capable</option>
                    <option value="not_capable">Not capable</option>
                  </select>
                  <label className={styles.pageSizeControl}>
                    Rows
                    <select
                      value={mappingPageSize}
                      onChange={(event) => {
                        setMappingPageSize(Number(event.target.value));
                        setMappingPage(1);
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </label>
                </div>

                {mappingsLoading ? (
                  <p>Loading mappings…</p>
                ) : filteredMappings.length === 0 ? (
                  <p className={styles.hint}>
                    {mappings.length === 0
                      ? 'No procedures mapped yet. Use the form above to add one.'
                      : 'No mapped procedures match the current filters.'}
                  </p>
                ) : (
                  <>
                    <div className={styles.tableWrap}>
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th className={styles.sortable} onClick={() => changeMappingSort('sha_procedure_code')}>
                              SHA Code{sortIndicator('sha_procedure_code')}
                            </th>
                            <th className={styles.sortable} onClick={() => changeMappingSort('procedure_name')}>
                              Procedure Name{sortIndicator('procedure_name')}
                            </th>
                            <th className={styles.sortable} onClick={() => changeMappingSort('is_capable')}>
                              Capable{sortIndicator('is_capable')}
                            </th>
                            <th className={styles.sortable} onClick={() => changeMappingSort('processing_time_minutes')}>
                              Processing Time{sortIndicator('processing_time_minutes')}
                            </th>
                            <th className={styles.sortable} onClick={() => changeMappingSort('notes')}>
                              Notes{sortIndicator('notes')}
                            </th>
                            <th title={KEYWORD_HELP}>Description Keywords</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedMappings.map((m) => (
                            <tr key={m.id}>
                              <td>
                                <code className={styles.code}>{m.sha_procedure_code}</code>
                              </td>
                              <td>{m.procedure_name || <span className={styles.hint}>—</span>}</td>
                              <td>
                                <span className={`${styles.capable} ${m.is_capable ? styles.capableYes : styles.capableNo}`}>
                                  {m.is_capable ? 'Yes' : 'No'}
                                </span>
                              </td>
                              <td>
                                {m.processing_time_minutes
                                  ? `${m.processing_time_minutes} min`
                                  : <span className={styles.hint}>—</span>}
                              </td>
                              <td>{m.notes || <span className={styles.hint}>—</span>}</td>
                              <td>
                                {editingKeywordsId === m.id ? (
                                  <div className={styles.keywordEditor}>
                                    <input
                                      autoFocus
                                      placeholder="e.g. *ray*, ultra*"
                                      value={keywordsDraft}
                                      onChange={(event) => setKeywordsDraft(event.target.value)}
                                      onKeyDown={(event) => {
                                        if (event.key === 'Enter') handleSaveKeywords(m);
                                        if (event.key === 'Escape') setEditingKeywordsId(null);
                                      }}
                                    />
                                    <button
                                      type="button"
                                      disabled={savingKeywordsId === m.id}
                                      onClick={() => handleSaveKeywords(m)}
                                    >
                                      {savingKeywordsId === m.id ? 'Saving…' : 'Save'}
                                    </button>
                                    <button
                                      type="button"
                                      className={styles.secondaryBtn}
                                      onClick={() => setEditingKeywordsId(null)}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    className={styles.keywordList}
                                    title="Edit description keywords"
                                    onClick={() => startEditingKeywords(m)}
                                  >
                                    {(m.description_keywords || []).length
                                      ? m.description_keywords.map((keyword) => (
                                        <code key={keyword} className={styles.code}>{keyword}</code>
                                      ))
                                      : <span className={styles.hint}>None · edit</span>}
                                  </button>
                                )}
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className={styles.removeBtn}
                                  disabled={removingId === m.id}
                                  onClick={() => handleRemoveMapping(m)}
                                >
                                  {removingId === m.id ? 'Removing…' : 'Remove'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className={styles.pagination}>
                      <span>
                        Showing {(currentMappingPage - 1) * mappingPageSize + 1}–
                        {Math.min(currentMappingPage * mappingPageSize, sortedMappings.length)}
                        {' '}of {sortedMappings.length}
                      </span>
                      <div>
                        <button
                          type="button"
                          disabled={currentMappingPage <= 1}
                          onClick={() => setMappingPage((page) => Math.max(1, page - 1))}
                        >
                          Previous
                        </button>
                        <span>Page {currentMappingPage} of {mappingTotalPages}</span>
                        <button
                          type="button"
                          disabled={currentMappingPage >= mappingTotalPages}
                          onClick={() => setMappingPage((page) => Math.min(mappingTotalPages, page + 1))}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Add mapping form */}
              <div className={`${styles.panel} ${styles.mappingFormPanel}`}>
                <h2>Add Procedure to {selectedModality.code}</h2>

                {error && <div className={styles.error}>{error}</div>}
                {successMsg && <div className={styles.success}>{successMsg}</div>}

                <form onSubmit={handleAddMapping} className={styles.form}>
                  <div className={styles.formRow}>
                    <label className={styles.label}>
                      Procedure Catalog <span className={styles.required}>*</span>
                    </label>
                    <SearchableSelect
                      options={availableProcedures}
                      value={form.procedure_id}
                      onChange={handleProcedureSelect}
                      required
                      disabled={proceduresLoading || procedureCatalogUnavailable || noAvailableProcedures}
                      placeholder={proceduresLoading ? 'Loading procedure catalog...' : 'Search procedure by code or name'}
                      emptyLabel={proceduresLoading ? 'Loading procedures' : 'Select procedure'}
                      helperLabel="procedures"
                      getOptionValue={(procedure) => procedure.id}
                      getOptionLabel={getProcedureLabel}
                      getSearchText={getProcedureSearchText}
                    />
                    {proceduresError && (
                      <span className={styles.errorText}>{proceduresError}</span>
                    )}
                    {procedureCatalogUnavailable && !proceduresError && (
                      <span className={styles.hint}>No procedures found in the catalog.</span>
                    )}
                    {noAvailableProcedures && (
                      <span className={styles.hint}>All loaded procedures are already mapped to this modality.</span>
                    )}
                  </div>

                  <div className={styles.formGrid}>
                    <div className={styles.formRow}>
                      <label className={styles.label}>
                        SHA Procedure Code <span className={styles.required}>*</span>
                      </label>
                      <input
                        required
                        placeholder="e.g. SHA-RAD-001"
                        value={form.sha_procedure_code}
                        onChange={(e) => setForm((prev) => ({ ...prev, sha_procedure_code: e.target.value }))}
                      />
                    </div>

                    <div className={styles.formRow}>
                      <label className={styles.label}>Processing Time (minutes)</label>
                      <input
                        type="number"
                        min="1"
                        placeholder="e.g. 30"
                        value={form.processing_time_minutes}
                        onChange={(e) => setForm((prev) => ({ ...prev, processing_time_minutes: e.target.value }))}
                      />
                    </div>

                    <div className={styles.formRow}>
                      <label className={styles.label}>Capability</label>
                      <label className={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={form.is_capable}
                          onChange={(e) => setForm((prev) => ({ ...prev, is_capable: e.target.checked }))}
                        />
                        Modality is capable of this procedure
                      </label>
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.label}>Description Keywords</label>
                    <input
                      placeholder="e.g. *ray*  or  US, ultra*, *sound*"
                      value={form.description_keywords}
                      onChange={(e) => setForm((prev) => ({ ...prev, description_keywords: e.target.value }))}
                    />
                    <span className={styles.hint}>{KEYWORD_HELP}</span>
                  </div>

                  <div className={styles.formRow}>
                    <label className={styles.label}>Notes</label>
                    <textarea
                      placeholder="Optional notes about this mapping…"
                      value={form.notes}
                      onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                    />
                  </div>

                  <div className={styles.formActions}>
                    <button type="submit" disabled={formLoading}>
                      {formLoading ? 'Adding…' : 'Add Mapping'}
                    </button>
                    <button
                      type="button"
                      className={styles.secondaryBtn}
                      onClick={() => { setForm(initialForm); setError(''); setSuccessMsg(''); }}
                    >
                      Reset
                    </button>
                  </div>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalityProcedureMappingPage;
